import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Alert,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { supabase } from '../../api/supabase';
import theme from '../../theme';
import { BackButton } from '../../components/ui/BackButton';

interface WorkshopRecord {
  license_plate: string;
  make_model: string;
  job_number: string;
  from: string;
  to: string;
  days: number;
  contract_type: string;
  work_type: string;
  credit_days?: number;
  daily_rate?: number;
  credit_amount?: number;
}

interface MonthData {
  year: number;
  month: number;
  name: string;
}

const AVAILABLE_MONTHS: MonthData[] = [
  { year: 2025, month: 8, name: 'August 2025' },
  { year: 2025, month: 9, name: 'September 2025' },
  { year: 2025, month: 10, name: 'October 2025' },
];

const WorkshopCreditReportScreen = ({ navigation }: any) => {
  const [selectedMonth, setSelectedMonth] = useState<MonthData>(AVAILABLE_MONTHS[0]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<Record<string, WorkshopRecord[]>>({});
  const [summary, setSummary] = useState<Record<string, any>>({});

  const getMonthOverlapPeriod = (
    startDate: string,
    endDate: string,
    targetYear: number,
    targetMonth: number
  ) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const monthStart = new Date(targetYear, targetMonth - 1, 1);
    const monthEnd = new Date(targetYear, targetMonth, 0);

    const overlapStart = start > monthStart ? start : monthStart;
    const overlapEnd = end < monthEnd ? end : monthEnd;

    if (overlapStart > overlapEnd) {
      return { from: '-', to: '-', days: 0 };
    }

    const days = Math.ceil((overlapEnd.getTime() - overlapStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return {
      from: overlapStart.toISOString().slice(0, 10),
      to: overlapEnd.toISOString().slice(0, 10),
      days,
    };
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      // Fetch job cards - data is already in job_cards table
      const { data: jobCards, error } = await supabase
        .from('job_cards')
        .select('id, job_number, license_plate, request_date, handover_date, return_date, job_status, make_model, year, under_maintenance, accident_repair, under_maintenance_al_qouz, under_maintenance_agency, others')
        .order('request_date');

      if (error) throw error;

      // Remove duplicates based on job_number (keep first occurrence)
      const uniqueJobCards = jobCards?.filter((jc, index, self) => 
        index === self.findIndex(j => j.job_number === jc.job_number)
      ) || [];

      // Fetch vehicle data for contract types
      const { data: vehicles } = await supabase
        .from('vehicles')
        .select('license_plate, contract_type, fleet_type');

      // Create vehicle lookup map
      const vehicleMap = new Map();
      if (vehicles) {
        vehicles.forEach(v => {
          vehicleMap.set(v.license_plate, v);
        });
      }

      // Fetch vehicle costs from vehicle_costs table
      const { data: vehicleCosts } = await supabase
        .from('vehicle_costs')
        .select('make_model, year, cost_per_month');

      // Create cost lookup map by make_model + year
      const costMap = new Map<string, number>();
      if (vehicleCosts) {
        vehicleCosts.forEach(vc => {
          const key = `${vc.make_model}_${vc.year}`;
          costMap.set(key, vc.cost_per_month || 0);
        });
      }

      // Process job cards
      const grouped: Record<string, WorkshopRecord[]> = {
        A: [],
        B: [],
        C1: [],
        C2: [],
        Other: [],
      };

      const summaryData: Record<string, any> = {
        A: { count: 0, days: 0, creditDays: 0, credit: 0 },
        B: { count: 0, days: 0, creditDays: 0, credit: 0 },
        C1: { count: 0, days: 0, creditDays: 0, credit: 0 },
        C2: { count: 0, days: 0, creditDays: 0, credit: 0 },
        Other: { count: 0, days: 0, creditDays: 0, credit: 0 },
      };

      // Step 1: Collect all job cards with their overlap periods
      const jobCardsWithOverlap: Array<{
        jc: any;
        veh: any;
        overlap: any;
        contract: string;
        workType: string;
      }> = [];

      // Helper function to get work types from job card
      const getWorkTypes = (jc: any) => {
        const types = [];
        if (jc.under_maintenance) types.push('Maintenance');
        if (jc.accident_repair) types.push('Accident');
        if (jc.under_maintenance_al_qouz) types.push('Al Qouz');
        if (jc.under_maintenance_agency) types.push('Agency');
        if (jc.others) types.push('Other');
        return types.length > 0 ? types.join(', ') : '-';
      };

      for (const jc of uniqueJobCards) {
        // Get vehicle info from vehicleMap by license plate
        const veh = vehicleMap.get(jc.license_plate) || { contract_type: 'Other', fleet_type: null };
        
        // Create combined vehicle object with data from both job_card and vehicles table
        const vehicleData = {
          license_plate: jc.license_plate,
          make_model: jc.make_model,
          year: jc.year,
          contract_type: veh.contract_type,
          fleet_type: veh.fleet_type
        };

        // Determine end date:
        // 1. If return_date exists -> car left workshop on that date
        // 2. If no return_date -> car is still in workshop (use today's date)
        const today = new Date().toISOString().slice(0, 10);
        const endDate = jc.return_date || today;
        
        const overlap = getMonthOverlapPeriod(jc.request_date, endDate, selectedMonth.year, selectedMonth.month);

        // FILTER: Only include records with at least 1 day in the target month
        if (overlap.days === 0) continue;

        const contract = veh.contract_type || 'Other';
        
        jobCardsWithOverlap.push({ jc, veh: vehicleData, overlap, contract, workType: getWorkTypes(jc) });
      }

      // Step 2: For Contract B, determine which cars get credit based on simultaneous count
      // Contract B rule: Credit applies when MORE than 9 cars are in workshop simultaneously
      // The most recent cars (beyond the 9th) get the credit
      
      const contractBCredits = new Map<string, { creditDays: number; creditAmount: number }>();
      
      if (jobCardsWithOverlap.some(item => item.contract === 'B')) {
        // Get all Contract B job cards in this month
        const contractBJobs = jobCardsWithOverlap.filter(item => item.contract === 'B');
        
        // For each day in the target month, count simultaneous Contract B cars
        const monthStart = new Date(selectedMonth.year, selectedMonth.month - 1, 1);
        const monthEnd = new Date(selectedMonth.year, selectedMonth.month, 0);
        
        // Track days each car qualifies for credit (day-by-day)
        const carCreditDays = new Map<string, number>();
        
        for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
          const currentDay = d.toISOString().slice(0, 10);
          
          // Find all Contract B cars in workshop on this specific day
          const carsInWorkshopToday = contractBJobs.filter(item => {
            const start = new Date(item.jc.request_date);
            const end = new Date(item.jc.return_date || new Date().toISOString().slice(0, 10));
            const day = new Date(currentDay);
            return start <= day && end >= day;
          });
          
          // Sort by entry date (oldest first) - first 9 get replacements
          carsInWorkshopToday.sort((a, b) => 
            new Date(a.jc.request_date).getTime() - new Date(b.jc.request_date).getTime()
          );
          
          // If more than 9 cars, the excess cars (most recent) get credit for this day
          if (carsInWorkshopToday.length > 9) {
            const excessCars = carsInWorkshopToday.slice(9); // Cars beyond the 9th
            excessCars.forEach(item => {
              const jobId = item.jc.job_number;
              // IMPORTANT: Only count this day if it's within the car's overlap period in this month
              const carOverlapStart = new Date(item.overlap.from);
              const carOverlapEnd = new Date(item.overlap.to);
              const currentDayDate = new Date(currentDay);
              
              if (currentDayDate >= carOverlapStart && currentDayDate <= carOverlapEnd) {
                carCreditDays.set(jobId, (carCreditDays.get(jobId) || 0) + 1);
              }
            });
          }
        }
        
        // Calculate credit amounts with 5-day grace period
        // Grace period only applies to the FIRST month of the job card (per workshop visit)
        const monthStartDate = new Date(selectedMonth.year, selectedMonth.month - 1, 1);
        
        // IMPORTANT: Initialize ALL Contract B jobs in the map, even if they have 0 credit days
        contractBJobs.forEach(job => {
          if (!carCreditDays.has(job.jc.job_number)) {
            // Car never qualified as excess (always within first 9) - set to 0
            contractBCredits.set(job.jc.job_number, { creditDays: 0, creditAmount: 0 });
          }
        });
        
        carCreditDays.forEach((daysEligible, jobNumber) => {
          const job = contractBJobs.find(item => item.jc.job_number === jobNumber);
          if (!job) return;
          
          // Check if this job card started before the target month
          const jobStartDate = new Date(job.jc.request_date);
          const isFirstMonth = jobStartDate >= monthStartDate;
          
          // Apply 5-day grace period only if this is the first month of the job card
          let creditDays: number;
          if (isFirstMonth) {
            // First month: apply grace period
            creditDays = daysEligible > 5 ? daysEligible - 5 : 0;
          } else {
            // Subsequent months: full credit (grace already applied in first month)
            creditDays = daysEligible;
          }
          
          if (creditDays > 0) {
            const costKey = `${job.veh.make_model}_${job.veh.year || 0}`;
            const monthlyRate = costMap.get(costKey) || 0;
            const dailyRate = monthlyRate / 30;
            const creditAmount = dailyRate * creditDays;
            contractBCredits.set(jobNumber, { creditDays, creditAmount });
          } else {
            contractBCredits.set(jobNumber, { creditDays: 0, creditAmount: 0 });
          }
        });
      }

      // Step 3: Build records with proper credit calculations
      for (const item of jobCardsWithOverlap) {
        const { jc, veh, overlap, contract, workType } = item;
        
        // Get vehicle cost from vehicle_costs table
        const costKey = `${veh.make_model}_${veh.year || 0}`;
        const monthlyRate = costMap.get(costKey) || 0;
        
        // Calculate daily rate (monthly cost / 30 days)
        const dailyRate = monthlyRate / 30;
        
        // Calculate credits based on contract type
        let creditDays: number | undefined;
        let creditAmount: number | undefined;
        
        if (contract === 'A') {
          // Contract A: 1-day grace period PER JOB CARD (not per month)
          // Check if this job card started before the target month
          const jobStartDate = new Date(jc.request_date);
          const monthStartDate = new Date(selectedMonth.year, selectedMonth.month - 1, 1);
          const isFirstMonth = jobStartDate >= monthStartDate;
          
          let contractACreditDays: number;
          if (isFirstMonth) {
            // First month: apply 1-day grace period
            contractACreditDays = overlap.days > 1 ? overlap.days - 1 : 0;
          } else {
            // Subsequent months: full credit (grace already applied in first month)
            contractACreditDays = overlap.days;
          }
          creditDays = contractACreditDays;
          creditAmount = dailyRate * contractACreditDays;
        } else if (contract === 'B') {
          // Contract B: Use pre-calculated credits based on simultaneous count
          const credit = contractBCredits.get(jc.job_number);
          if (credit) {
            creditDays = credit.creditDays;
            creditAmount = credit.creditAmount;
          } else {
            // Should not happen since we initialize all Contract B jobs, but fallback to 0
            creditDays = 0;
            creditAmount = 0;
          }
        }

        const record: WorkshopRecord = {
          license_plate: jc.license_plate,
          make_model: veh.make_model,
          job_number: jc.job_number,
          from: overlap.from,
          to: overlap.to,
          days: overlap.days,
          contract_type: contract,
          work_type: workType,
          credit_days: creditDays,
          daily_rate: dailyRate > 0 ? dailyRate : undefined,
          credit_amount: creditAmount,
        };

        const group = ['A', 'B', 'C1', 'C2'].includes(contract) ? contract : 'Other';
        grouped[group].push(record);

        summaryData[group].count++;
        summaryData[group].days += overlap.days;
        if (creditDays !== undefined) {
          summaryData[group].creditDays += creditDays;
        }
        if (['A', 'B'].includes(contract) && creditAmount) {
          summaryData[group].credit += creditAmount;
        }
      }

      setReportData(grouped);
      setSummary(summaryData);
    } catch (err: any) {
      Alert.alert('Error', err.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (Platform.OS !== 'web') {
      Alert.alert('Not Available', 'CSV export is only available on web');
      return;
    }

    // Dynamic headers and rows based on contract type
    let csv = '';
    
    // Add summary section at the top
    csv += 'SUMMARY\n';
    csv += 'Contract Type,Vehicle Count,Total Workshop Days,Total Credit Days,Total Credit Amount\n';
    Object.entries(summary).forEach(([contract, data]) => {
      if (data.count > 0) {
        const creditAmount = ['A', 'B'].includes(contract) ? data.credit.toFixed(2) : 'N/A';
        csv += `${contract},${data.count},${data.days},${data.creditDays},${creditAmount}\n`;
      }
    });
    csv += '\n';
    
    Object.entries(reportData).forEach(([contract, records]) => {
      if (records.length === 0) return;
      
      // For Contract A and B: include all columns with credit info
      if (['A', 'B'].includes(contract)) {
        if (csv === '') {
          csv = 'Contract Type,License Plate,Make/Model,Job Number,Work Type,From,To,Days,Credit Days,Daily Rate,Credit Amount\n';
        }
        records.forEach(r => {
          csv += `${contract},${r.license_plate},${r.make_model},${r.job_number},${r.work_type},${r.from},${r.to},${r.days},${r.credit_days !== undefined ? r.credit_days : '-'},${r.daily_rate?.toFixed(2) || ''},${r.credit_amount?.toFixed(2) || ''}\n`;
        });
      } else {
        // For Contract C1, C2, Other: exclude credit columns
        if (csv === '' || !csv.includes('Credit Days')) {
          // Start new section without credit columns
          csv += '\nContract Type,License Plate,Make/Model,Job Number,Work Type,From,To,Days\n';
        }
        records.forEach(r => {
          csv += `${contract},${r.license_plate},${r.make_model},${r.job_number},${r.work_type},${r.from},${r.to},${r.days}\n`;
        });
      }
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Workshop_Credit_Report_${selectedMonth.name.replace(' ', '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToPDF = () => {
    if (Platform.OS !== 'web') {
      Alert.alert('Not Available', 'PDF export is only available on web');
      return;
    }

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Workshop Credit Report - ${selectedMonth.name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #333; }
          h2 { color: #555; margin-top: 30px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #4CAF50; color: white; }
          .summary { background-color: #f0f0f0; padding: 10px; margin: 10px 0; border-radius: 5px; }
          .main-summary { background-color: #2196F3; color: white; padding: 15px; margin: 20px 0; border-radius: 5px; }
          .main-summary h2 { color: white; margin-top: 0; }
          .main-summary table { background-color: white; }
          .main-summary th { background-color: #1976D2; }
        </style>
      </head>
      <body>
        <h1>Workshop Credit Report - ${selectedMonth.name}</h1>
        
        <div class="main-summary">
          <h2>WORKSHOP CREDIT SUMMARY</h2>
          <table>
            <tr>
              <th>Contract Type</th>
              <th>Vehicle Count</th>
              <th>Total Workshop Days</th>
              <th>Total Credit Days</th>
              <th>Total Credit Amount</th>
            </tr>`;
    
    Object.entries(summary).forEach(([contract, data]) => {
      if (data.count > 0) {
        const creditAmount = ['A', 'B'].includes(contract) ? `${data.credit.toFixed(2)} AED` : 'N/A';
        html += `
            <tr>
              <td><strong>Contract ${contract}</strong></td>
              <td>${data.count}</td>
              <td>${data.days}</td>
              <td>${data.creditDays}</td>
              <td>${creditAmount}</td>
            </tr>`;
      }
    });
    
    html += `
          </table>
        </div>
    `;

    Object.entries(reportData).forEach(([contract, records]) => {
      if (records.length === 0) return;

      html += `<h2>Contract ${contract}</h2>`;
      html += `<table>
        <tr>
          <th>License Plate</th>
          <th>Make/Model</th>
          <th>Job Number</th>
          <th>Work Type</th>
          <th>From</th>
          <th>To</th>
          <th>Days</th>
          ${['A', 'B'].includes(contract) ? '<th>Credit Days</th><th>Daily Rate</th><th>Credit Amount</th>' : ''}
        </tr>`;

      records.forEach(r => {
        html += `<tr>
          <td>${r.license_plate}</td>
          <td>${r.make_model}</td>
          <td>${r.job_number}</td>
          <td>${r.work_type}</td>
          <td>${r.from}</td>
          <td>${r.to}</td>
          <td>${r.days}</td>
          ${['A', 'B'].includes(contract) ? `<td>${r.credit_days || ''}</td><td>${r.daily_rate?.toFixed(2) || ''}</td><td>${r.credit_amount?.toFixed(2) || ''}</td>` : ''}
        </tr>`;
      });

      html += `</table>`;

      const sum = summary[contract];
      if (sum) {
        html += `<div class="summary">
          <strong>Summary:</strong> ${sum.count} vehicles • ${sum.days} workshop days • ${sum.creditDays} credit days${['A', 'B'].includes(contract) ? ` • ${sum.credit.toFixed(2)} AED credit` : ''}
        </div>`;
      }
    });

    html += `
        <button onclick="window.print()">Print / Save as PDF</button>
      </body>
      </html>
    `;

    const printWindow = window.open('', '', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
    }
  };

  return (
    <View style={styles.container}>
      <BackButton onPress={() => navigation.goBack()} style={styles.backButton} />

      <View style={styles.header}>
        <Text style={styles.title}>Workshop Credit Report</Text>
      </View>

      <View style={styles.controls}>
        <View style={styles.pickerContainer}>
          <Text style={styles.label}>Select Month:</Text>
          <Picker
            selectedValue={selectedMonth.name}
            onValueChange={(itemValue) => {
              const month = AVAILABLE_MONTHS.find(m => m.name === itemValue);
              if (month) setSelectedMonth(month);
            }}
            style={styles.picker}
          >
            {AVAILABLE_MONTHS.map(m => (
              <Picker.Item key={m.name} label={m.name} value={m.name} />
            ))}
          </Picker>
        </View>

        <TouchableOpacity style={styles.generateButton} onPress={generateReport} disabled={loading}>
          {loading ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.buttonText}>Generate Report</Text>
          )}
        </TouchableOpacity>
      </View>

      {Object.keys(reportData).length > 0 && (
        <>
          <View style={styles.exportButtons}>
            <TouchableOpacity style={styles.exportButton} onPress={exportToCSV}>
              <Text style={styles.exportButtonText}>Export CSV</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.exportButton, styles.pdfButton]} onPress={exportToPDF}>
              <Text style={styles.exportButtonText}>Export PDF</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.reportContainer}>
            {/* Summary Table */}
            <View style={styles.summaryTableSection}>
              <Text style={styles.summaryTableTitle}>WORKSHOP CREDIT SUMMARY</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={true}>
                <View style={styles.table}>
                  <View style={styles.tableHeader}>
                    <Text style={[styles.tableCell, styles.headerCell, { width: 120 }]}>Contract Type</Text>
                    <Text style={[styles.tableCell, styles.headerCell, { width: 120 }]}>Vehicle Count</Text>
                    <Text style={[styles.tableCell, styles.headerCell, { width: 150 }]}>Total Workshop Days</Text>
                    <Text style={[styles.tableCell, styles.headerCell, { width: 140 }]}>Total Credit Days</Text>
                    <Text style={[styles.tableCell, styles.headerCell, { width: 150 }]}>Total Credit Amount</Text>
                  </View>
                  
                  {Object.entries(summary).map(([contract, data]) => {
                    if (data.count === 0) return null;
                    return (
                      <View key={contract} style={styles.tableRow}>
                        <Text style={[styles.tableCell, { width: 120, fontWeight: 'bold' }]}>Contract {contract}</Text>
                        <Text style={[styles.tableCell, { width: 120 }]}>{data.count}</Text>
                        <Text style={[styles.tableCell, { width: 150 }]}>{data.days}</Text>
                        <Text style={[styles.tableCell, { width: 140 }]}>{data.creditDays}</Text>
                        <Text style={[styles.tableCell, { width: 150 }]}>
                          {['A', 'B'].includes(contract) ? `${data.credit.toFixed(2)} AED` : 'N/A'}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              </ScrollView>
            </View>

            {/* Detailed Contract Sections */}
            {Object.entries(reportData).map(([contract, records]) => {
              if (records.length === 0) return null;

              return (
                <View key={contract} style={styles.contractSection}>
                  <Text style={styles.contractTitle}>Contract {contract}</Text>

                  <ScrollView horizontal showsHorizontalScrollIndicator={true}>
                    <View style={styles.table}>
                      <View style={styles.tableHeader}>
                        <Text style={[styles.tableCell, styles.headerCell, { width: 100 }]}>License Plate</Text>
                        <Text style={[styles.tableCell, styles.headerCell, { width: 150 }]}>Make/Model</Text>
                        <Text style={[styles.tableCell, styles.headerCell, { width: 120 }]}>Job Number</Text>
                        <Text style={[styles.tableCell, styles.headerCell, { width: 140 }]}>Work Type</Text>
                        <Text style={[styles.tableCell, styles.headerCell, { width: 100 }]}>From</Text>
                        <Text style={[styles.tableCell, styles.headerCell, { width: 100 }]}>To</Text>
                        <Text style={[styles.tableCell, styles.headerCell, { width: 60 }]}>Days</Text>
                        {['A', 'B'].includes(contract) && (
                          <>
                            <Text style={[styles.tableCell, styles.headerCell, { width: 80 }]}>Credit Days</Text>
                            <Text style={[styles.tableCell, styles.headerCell, { width: 100 }]}>Daily Rate</Text>
                            <Text style={[styles.tableCell, styles.headerCell, { width: 120 }]}>Credit Amount</Text>
                          </>
                        )}
                      </View>

                      {records.map((record, idx) => (
                        <View key={idx} style={styles.tableRow}>
                          <Text style={[styles.tableCell, { width: 100 }]}>{record.license_plate}</Text>
                          <Text style={[styles.tableCell, { width: 150 }]}>{record.make_model}</Text>
                          <Text style={[styles.tableCell, { width: 120 }]}>{record.job_number}</Text>
                          <Text style={[styles.tableCell, { width: 140 }]}>{record.work_type}</Text>
                          <Text style={[styles.tableCell, { width: 100 }]}>{record.from}</Text>
                          <Text style={[styles.tableCell, { width: 100 }]}>{record.to}</Text>
                          <Text style={[styles.tableCell, { width: 60 }]}>{record.days}</Text>
                          {['A', 'B'].includes(contract) && (
                            <>
                              <Text style={[styles.tableCell, { width: 80 }]}>{record.credit_days || '-'}</Text>
                              <Text style={[styles.tableCell, { width: 100 }]}>{record.daily_rate?.toFixed(2) || '-'}</Text>
                              <Text style={[styles.tableCell, { width: 120 }]}>{record.credit_amount?.toFixed(2) || '-'}</Text>
                            </>
                          )}
                        </View>
                      ))}
                    </View>
                  </ScrollView>

                  {summary[contract] && (
                    <View style={styles.summaryBox}>
                      <Text style={styles.summaryText}>
                        Total: {summary[contract].count} vehicles • {summary[contract].days} workshop days • {summary[contract].creditDays} credit days
                        {['A', 'B'].includes(contract) && ` • ${summary[contract].credit.toFixed(2)} AED credit`}
                      </Text>
                    </View>
                  )}
                </View>
              );
            })}
          </ScrollView>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  backButton: {
    position: 'absolute',
    top: 20,
    left: 20,
    zIndex: 10,
  },
  header: {
    backgroundColor: theme.colors.white,
    padding: theme.spacing.lg,
    paddingTop: 60,
    ...theme.shadows.sm,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: theme.colors.textPrimary,
  },
  controls: {
    backgroundColor: theme.colors.white,
    padding: theme.spacing.md,
    marginTop: theme.spacing.sm,
  },
  pickerContainer: {
    marginBottom: theme.spacing.md,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  picker: {
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.md,
    height: 50,
  },
  generateButton: {
    backgroundColor: theme.colors.primary,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
    ...theme.shadows.sm,
  },
  buttonText: {
    color: theme.colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  exportButtons: {
    flexDirection: 'row',
    padding: theme.spacing.md,
    gap: theme.spacing.sm,
  },
  exportButton: {
    flex: 1,
    backgroundColor: theme.colors.success,
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
  },
  pdfButton: {
    backgroundColor: theme.colors.error,
  },
  exportButtonText: {
    color: theme.colors.white,
    fontSize: 14,
    fontWeight: '600',
  },
  reportContainer: {
    flex: 1,
    padding: theme.spacing.md,
  },
  summaryTableSection: {
    backgroundColor: theme.colors.info,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.lg,
    ...theme.shadows.md,
  },
  summaryTableTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: theme.colors.white,
    marginBottom: theme.spacing.md,
    textAlign: 'center',
  },
  contractSection: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
    ...theme.shadows.sm,
  },
  contractTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  table: {
    borderWidth: 1,
    borderColor: theme.colors.gray200,
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: theme.colors.primary,
  },
  tableRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray200,
  },
  tableCell: {
    padding: theme.spacing.sm,
    fontSize: 12,
  },
  headerCell: {
    color: theme.colors.white,
    fontWeight: '600',
  },
  summaryBox: {
    backgroundColor: theme.colors.gray50,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    marginTop: theme.spacing.md,
  },
  summaryText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
});

export default WorkshopCreditReportScreen;
